import React, { useEffect, useRef, useState } from 'react';

import Icon from 'views/site/Icon.js';
import Modal from 'views/site/Modal.js';
import SupportFlowModal from 'views/site/SupportFlowModal.js';
import ProductManuals from 'views/site/ProductManuals.js';

import { animated, useSpring } from '@react-spring/web';
import moment from 'moment-timezone';
import update from 'immutability-helper';

// Appearance, Button, LottieView, PageControl and TextField are referenced
// only by the ~710-line GraciAssistant block that is commented out below.
// eslint reports them as unused because they are — grep will tell you
// otherwise, but grep does not read comments.
import Appearance from 'styles/Appearance.js';
import Catalog from 'files/Catalog.js';
import SiteButton from 'views/site/Button.js';
import Theme from 'styles/Theme.js';
import Button from 'views/Button.js';
import LottieView from 'views/Lottie.js';
import { NavigationSectionHeader } from 'managers/Components.js';
import PageControl from 'views/PageControl.js';
import Request from 'files/Request.js';
import TextField from 'views/TextField.js';
import Utils from 'files/Utils.js';
//import { ViewCardHeader, ViewEntry } from 'views/Main.js';

/*
export const GraciAssistant = ({ input }, { index, options, utils }) => {

    const layerID = 'graci_assistant';
    const scrollViewRef = useRef(null);

    const [height, setHeight] = useState(0);
    const [id, setID] = useState(null);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState(null);

    const onNewOutput = evt => {

        // end loading, set completion id if an id was provided, and update state with messages
        setID(id => evt.id || id);
        setLoading(false);

        // update local state with messages data
        setMessages(messages => {
            let results = messages.filter(message => message.loading !== true);
            if(evt.error) {
                return results.concat([{
                    error: evt.error,
                    role: 'system'
                }]);
            }
            if(evt.messages) {
                return evt.messages.map(message => {
                    if(message.role !== 'user' && message.visible !== false) {
                        try {
                            message.content = JSON.parse(message.content);
                        } catch(e) {
                            console.error(e.message);
                        }
                    }
                    return message;
                });
            }
            return results;
        });
    }

    const onSendMessage = async () => {
        try {

            setLoading('submit');
            if(!text || text.length === 0) {
                throw new Error('Please enter a message before moving on');
            }

            // clear text field for next input
            setText(null);

            // add message for user and system to list of messages, this is later replaced by a server event
            setMessages(messages => {
                return messages.concat([{
                    content: text,
                    role: 'user'
                }]);
            });

            // add system loading event after a brief delay
            await Utils.sleep(0.5);
            setMessages(messages => {
                return messages.concat([{
                    loading: true,
                    role: 'system'
                }]);
            });

            // emit a new message input event to the websockets server
            utils.sockets.emit('graci', 'new_chat_completion_message_input', {
                id: id,
                messages: [text],
                session: getSessionVariables()
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Just a Second',
                message: e.message || 'An unknown error occurred'
            });
        }
    }

    const onShowChatHistory = () => {
        utils.layer.open({
            Component: GraciAssistantChatHistory.bind(this, {
                onChange: chat => {
                    setID(chat.id);
                    setMessages(chat.metadata.messages || []);
                }
            }),
            id: 'graci_assistant_chat_history'
        });
    }

    const getMessages = () => {

        // prepare list of message targets
        let targets = messages.filter(message => message.visible !== false && message.role !== 'tool');
        if(targets.length === 0) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    height: height,
                    justifyContent: 'center',
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={require('files/lottie/graci-ai-spinner.json')}
                    style={{
                        height: 150,
                        marginBottom: 12,
                        width: 150
                    }}/>
                    <div style={{
                        backgroundColor: Appearance.colors.softBorder(),
                        borderBottomLeftRadius: 12,
                        borderBottomRightRadius: 12,
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 12,
                        marginBottom: 8,
                        padding: '10px 16px 10px 16px'
                    }}>
                        <span style={{
                            ...Appearance.textStyles.header()
                        }}>{`Hi, I'm GRACI`}</span>
                    </div>
                    <div style={{
                        backgroundColor: Appearance.colors.softBorder(),
                        borderBottomLeftRadius: 12,
                        borderBottomRightRadius: 12,
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 12,
                        marginBottom: 4,
                        padding: '12px 16px 12px 16px'
                    }}>
                        <span style={{
                            ...Appearance.textStyles.title()
                        }}>
                            {'I can anwswer questions about your home safe network and help you troubleshoot issues with your sensors.'}
                        </span>
                    </div>
                </div>
            )
        }

        return targets.map((message, index) => {
            return (
                <Message 
                data={message} 
                key={index}
                utils={utils} />
            )
        });
    }

    const getSessionVariables = () => {
        let dealership = utils.dealership.get();
        return {
            dealership_id: dealership.id,
            dealership_name: dealership.name,
            platform: 'web'
        }
    }

    const getTextInput = () => {

        let canSendMessage = loading === 'submit' || text?.length === 0 ? false : true;
        return (
            <div style={{
                alignItems: 'center',
                borderTopColor: Appearance.colors.divider(),
                borderTopWidth: 1,
                display: 'flex',
                flexDirection: 'row',
                padding: 16,
                width: '100%'
            }}>
                <TextField 
                placeholder={'Ask me anything about your network...'}
                onChange={setText}
                value={text} />
                <div
                className={canSendMessage ? 'text-button' : ''}
                onClick={canSendMessage ? onSendMessage : null}
                style={{
                    backgroundColor: canSendMessage ? Appearance.colors.primary(utils) : Appearance.colors.grey(),
                    borderRadius: 17.5,
                    marginLeft: 8
                }}>
                    <img 
                    src={canSendMessage ? 'images/send-arrow-button-clear.png' : 'images/ai-sending-button-clear.png'}
                    style={{
                        height: 35,
                        width: 35
                    }} />
                </div>
            </div>
        )
    }

    const connectToSockets = async () => {
        try {
            await utils.sockets.on('graci', 'new_chat_completion_message_output', onNewOutput);
        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `We were unable to establish a connection to the GRACI assistant. ${e.message || 'An unknown error occurred'}`,
                onClick: setLayerState.bind(this, 'close')
            });
        }
    }

    const disconnectFromSockets = async () => {
        try {
            await utils.sockets.off('graci', 'new_chat_completion_message_output', onNewOutput);
        } catch(e) {
            console.error(e.message);
        }
    }

    const initCompletionWithCustomInput = () => {

        // set placeholder loading message while request is submitted and processed
        setLoading('submit');
        setMessages([{
            content: { text: input.message },
            role: 'system'
        },{
            loading: true,
            role: 'system'
        }]);

        // submit new input request to server and start chat completion
        utils.sockets.emit('graci', 'new_chat_completion_message_input', {
            instructions: input.instructions,
            session: getSessionVariables()
        });
    }

    useEffect(() => {
        scrollViewRef.current.scrollToEnd({ animated: true });
    }, [messages]);

    useEffect(() => {
        if(input) {
            initCompletionWithCustomInput();
        }
    }, [input]);

    useEffect(() => {
        connectToSockets();
        return disconnectFromSockets;
    }, []);

    return (
        <Layer 
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            onHeightChange: setHeight,
            removePadding: true,
            secondaryButton: {
                color: Appearance.colors.primary(utils),
                onClick: onShowChatHistory,
                text: 'History'
            },
            sticky: {
                bottom: getTextInput()
            }
        }}
        ref={scrollViewRef}
        utils={utils}>
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                padding: 16,
                width: '100%'
            }}>
                {getMessages()}
            </div>
        </Layer>
    )
}

export const GraciAssistantChatHistory = ({ onChange }, { index, options, utils }) => {

    const layerID = 'graci_assistant_chat_history';
    const limit = 10;
    const offset = useRef(0);

    const [chats, setChats] = useState([]);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paging, setPaging] = useState(null);
    const theme = utils.theme.get();

    const onChatClick = async id => {
        try {

            setLoading(id);
            let { chat } = await Request.get(utils, '/users/', {
                id: id,
                type: 'openai_chat_completion_details'
            });
            
            setLayerState('close');
            if(typeof(onChange) === 'function') {
                onChange(chat);
            }
            
        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving your the details for this chat. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const getContent = () => {
        if(loading === true) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    height: 50
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    duration={2500}
                    style={{
                        height: 40,
                        width: 40
                    }}/>
                </div>
            )
        }
        if(chats.length === 0) {
            return (
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <ViewEntry 
                    bottomBorder={false}
                    hideIcon={true}
                    subTitle={'We were unable to find any chats for your device'}
                    title={'No Chats Found'} 
                    theme={theme}
                    utils={utils}/>
                </div>
            )
        }
        return chats.map((chat, index) => {
            return (
                <ViewEntry 
                bottomBorder={index !== chats.length - 1}
                icon={{
                    path: require('images/graci-assistant-message-icon.png')
                }}
                key={index}
                loading={loading === chat.id}
                onClick={onChatClick.bind(this, chat.id)}
                subTitle={Utils.format.date(chat.date)}
                title={chat.name} 
                theme={theme}
                utils={utils}/>
            )
        })
    }

    const fetchChats = async () => {
        try {
            let { chats, paging } = await Request.get(utils, '/users/', {
                limit: limit,
                offset: offset.current,
                type: 'openai_chat_completions_history'
            });

            setLoading(false);
            setPaging(paging);
            setChats(chats.map(chat => ({
                ...chat,
                date: moment.utc(chat.date).local()
            })));

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving your chat history. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {
        fetchChats();
    }, []);

    return (
        <Layer 
        id={layerID}
        index={index}
        options={{
            ...options,
            bottomCard: true,
            layerState: layerState,
            loading: loading === true
        }}
        utils={utils}>
            <ViewCardHeader
            body={`These are all the chats that you've started for your network. Tap on a chat to view your conversation.`}
            title={'Chat History'} />
            <div style={Appearance.styles.unstyledPanel()}>
                {getContent()}
            </div>
            {paging && (
                <PageControl
                data={paging}
                limit={limit}
                loading={loading === 'paging'}
                offset={offset.current}
                style={{
                    borderTopWidth: 0
                }}
                onClick={next => {
                    offset.current = next;
                    setLoading('paging');
                    fetchChats();
                }} 
                utils={utils} />
            )}
        </Layer>
    )
}

const Message = ({ data, utils }) => {

    const [animations, animationsApi] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        opacity: 0,
        top: -50
    }));
    const theme = utils.theme.get();

    const onItemClick = item => {
        switch(item.key?.toLowerCase()) {
            case 'address':
            window.open(`https://www.google.com/maps/place/${encodeURIComponent(item.value)}`)
            break;

            case 'email':
            case 'email address':
            window.open(`mailto:${item.value}`);
            break;

            case 'phone':
            case 'phone number':
            window.open(`tel:${item.value}`);
            break;

            case 'website':
            window.open(item.value);
            break;
        }
    }

    const getBackgroundColor = () => {
        if(data.role === 'user') {
            return Appearance.colors.primary(utils);
        }
        if(data.error) {
            return Appearance.colors.red;
        }
        return Appearance.colors.softBorder();
    }

    const getValueColor = item => {
        if(item.value.toLowerCase() === 'offline') {
            return Appearance.colors.red;
        }
        if(item.key.toLowerCase().includes('emergency') === true && item.value.toLowerCase() === 'yes') {
            return Appearance.colors.red;
        }
        if(item.value.toLowerCase() === 'online') {
            return Appearance.colors.green;
        }
        return Appearance.colors.subText();
    }
    

    const getContent = () => {
        if(data.visible === false) {
            return null;
        }
        if(data.error) {
            return getMessageContainer((
                <div style={{
                    padding: '10px 16px 10px 16px'
                }}>
                    <span style={{
                        ...Appearance.textStyles.title(),
                        color: 'white'
                    }}>{data.error}</span>
                </div>
            ))
        }
        if(data.loading === true) {
            return getMessageContainer((
                <div style={{
                    padding: '4px 16px 4px 16px'
                }}>
                    <LottieView
                    autoPlay={true}
                    duration={2000}
                    loop={true}
                    source={require('files/lottie/dots-grey.json')}
                    style={{
                        height: 35,
                        width: 35
                    }}/>
                </div>
            ))
        }
        if(data.content) {
            if(data.role === 'user') {
                return getMessageContainer((
                    <div style={{
                         padding: '10px 16px 10px 16px'
                    }}>
                        <span style={{
                            ...Appearance.textStyles.title(),
                            color: data.role === 'user' ? 'white' : Appearance.colors.text()
                        }}>{data.content || ''}</span>
                    </div>
                ))
            }
            let { data_input_request, sections = [], text } = data.content;
            return (
                <>
                {text?.length > 0 && 
                    getMessageContainer((
                        <div style={{
                            padding: '10px 16px 10px 16px'
                        }}>
                            <span style={{
                                ...Appearance.textStyles.title(),
                                color: Appearance.colors.text()
                            }}>{text}</span>
                        </div>
                    ))
                }
                {data_input_request?.length > 0 && 
                    getMessageContainer((
                        <div style={{
                            padding: '10px 16px 10px 16px'
                        }}>
                            <span style={{
                                ...Appearance.textStyles.title(),
                                color: Appearance.colors.text()
                            }}>{data_input_request}</span>
                        </div>
                    ))
                }
                {(sections || []).map((section, index, sections) => {
                    return (
                        <div 
                        key={index}
                        style={{
                            marginBottom: index !== sections.length - 1 ? 8 : 0,
                        }}>
                            {getMessageContainer((
                                <>
                                <div style={{
                                    padding: '10px 16px 10px 16px',
                                    width: '100%'
                                }}>
                                    <span style={{
                                        ...Appearance.textStyles.title(),
                                        marginBottom: 2
                                    }}>{section.header}</span>
                                    <span style={{
                                        ...Appearance.textStyles.subTitle()
                                    }}>{section.sub_header}</span>
                                </div>
                                {Array.isArray(section.list_items) && section.list_items.map((item, index) => {
                                    let pressable = shouldAcceptClicks(item);
                                    return (
                                        <div
                                        className={pressable ? 'text-button' : ''} 
                                        key={index}
                                        onClick={pressable ? onItemClick.bind(this, item) : null}
                                        style={{
                                            alignItems: 'center',
                                            borderBottomColor: Appearance.colors.divider(),
                                            borderBottomWidth: 1,
                                            display: 'flex',
                                            flexDirection: 'row',
                                            minWidth: 0,
                                            paddingHorizontal: 16,
                                            paddingVertical: 10,
                                            width: '100%'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                flexGrow: 1,
                                                flexShrink: 1,
                                                minWidth: 0
                                            }}>
                                                <span style={{
                                                    ...Appearance.textStyles.key(),
                                                    color: Appearance.colors.text(),
                                                    marginBottom: 2
                                                }}>{item.key}</span>
                                                <span style={{
                                                    ...Appearance.textStyles.key(),
                                                    color: getValueColor(item)
                                                }}>{item.value}</span>
                                            </div>
                                            {pressable === true && (
                                                <img
                                                src={'images/next-arrow-grey-small.png'}
                                                style={{
                                                    height: 12,
                                                    marginLeft: 12,
                                                    opacity: 0.75,
                                                    objectFit: 'contain',
                                                    width: 12
                                                }} />
                                            )}
                                        </div>
                                    )
                                })}
                                </>
                            ))}
                            {(section.video_tutorial_links || []).map((link, index) => {
                                return getMessageContainer((
                                    <ViewEntry 
                                    bottomBorder={false}
                                    icon={{
                                        path: require('images/graci-assistant-message-icon.png')
                                    }}
                                    onClick={() => window.open(link.url)}
                                    subTitle={'Tap to open link in your browser'}
                                    title={link.title}
                                    theme={theme}
                                    utils={utils} />
                                ), index)
                            })}
                        </div>
                    )
                })}
                </>
            )
        }
    }

    const getMessageContainer = (children, key) => {
        return (
            <animated.View 
            key={key}
            style={{
                alignSelf: data.role === 'user' ? 'flex-end' : 'flex-start',
                backgroundColor: getBackgroundColor(),
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12,
                borderTopLeftRadius: data.role === 'user' ? 12 : 0,
                borderTopRightRadius: data.role === 'user' ? 0 : 12,
                marginBottom: 16,
                maxWidth: '75%',
                position: 'relative',
                ...animations
            }}>
                {children}
            </animated.View>
        )
    }

    const shouldAcceptClicks = item => {
        return item.value?.toLowerCase().includes('please provide') === false && ['address','email','email_address','phone','phone number','website'].includes(item.key?.toLowerCase());
    }

    useEffect(() => {
        animationsApi.start({
            opacity: 1,
            top: 0
        });
    }, []);
    
    return getContent();
}
*/

export const GraciSupportContent = ({ assets, containerStyle, headingShown, style, title, values = [] }) => {

    /*
     * `headingShown` is whatever the surface above already put on screen, so
     * this can avoid saying it a second time. In a modal that is the article
     * title in the header; inline on the page it is nothing.
     */
    const sameAsArticle = value => {
        return Boolean(headingShown) && String(value).trim().toLowerCase() === String(headingShown).trim().toLowerCase();
    };

    /*
     * A support article, rewritten in the marketing surface's design system.
     * It was the last thing on the page still drawing with Appearance — an
     * uppercase LayerItem heading per asset, bodyText in the legacy face, and
     * bootstrap grid rows for the transcript — which is why the page still read
     * as the old site once you got past the search bar.
     *
     * Two behavioural fixes came with it:
     *
     *   The article's own text never rendered. getContent() tested
     *   `entries.elngth > 0`, and undefined > 0 is false, so every article has
     *   been silently dropping its body copy.
     *
     *   The transcript was one-way. Once opened it could not be closed again,
     *   because visibility was only ever set to true.
     */

    const mobile = Utils.isMobile();

    const [animations, animationsApi] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        opacity: 0,
        transform: 'scale(0.75)'
    }));
    const [fullscreenContent, setFullscreenContent] = useState(null);
    const [transcriptVisibility, setTranscriptVisibility] = useState({});

    const onCloseFullscreenContentClick = () => {
        animationsApi.start({
            to: async next => {
                await next({
                    opacity: 0,
                    transform: 'scale(0.75)'
                });
                setFullscreenContent(null);
            }
        });
    }

    const onTranscriptVisibilityClick = index => {
        setTranscriptVisibility(props => update(props, {
            [index]: {
                $set: props[index] !== true
            }
        }));
    }

    const getText = (entry, key) => (
        <span
        key={key}
        style={{
            ...Theme.textStyles.body({ fontSize: mobile ? 15 : 16, lineHeight: 1.7 }),
            display: 'block',
            whiteSpace: 'normal',
            ...entry.style
        }}>{entry.text}</span>
    );

    const getContent = () => {
        return values?.length > 0 && (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
                marginBottom: assets?.length > 0 ? (mobile ? 28 : 36) : 0,
                width: '100%'
            }}>
                {values.map((entry, index) => getText(entry, index))}
            </div>
        )
    }

    const getFullscreenButton = asset => (
        <button
        aria-label={'View full size'}
        onClick={setFullscreenContent.bind(this, asset)}
        style={{
            alignItems: 'center',
            backgroundColor: 'rgba(0,0,0,0.55)',
            border: 'none',
            borderRadius: 999,
            bottom: 12,
            cursor: 'pointer',
            display: 'flex',
            height: 36,
            justifyContent: 'center',
            position: 'absolute',
            right: 12,
            width: 36
        }}>
            <Icon name={'arrows-fullscreen'} size={14} color={'#FFFFFF'} />
        </button>
    );

    const getVideoTranscript = index => {

        let asset = assets[index];
        if(!asset.transcript) {
            return null;
        }

        let open = transcriptVisibility[index] === true;

        return (
            <div style={{
                border: `1px solid ${Theme.colors.border()}`,
                borderRadius: 14,
                marginTop: 14,
                overflow: 'hidden',
                width: '100%'
            }}>
                <button
                onClick={onTranscriptVisibilityClick.bind(this, index)}
                style={{
                    alignItems: 'center',
                    backgroundColor: Theme.colors.offWhite,
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    gap: 12,
                    justifyContent: 'space-between',
                    padding: mobile ? '14px 16px' : '15px 20px',
                    textAlign: 'left',
                    width: '100%'
                }}>
                    <span style={{
                        alignItems: 'center',
                        display: 'inline-flex',
                        gap: 10
                    }}>
                        <Icon name={'card-text'} size={14} color={Theme.colors.primary()} />
                        <span style={Theme.textStyles.body({ color: Theme.colors.text(), fontSize: 15, fontWeight: 600 })}>
                            {open ? 'Hide transcript' : 'Read the transcript'}
                        </span>
                    </span>
                    <Icon name={'chevron-down'} size={12} color={Theme.colors.primary()} style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.25s ease' }} />
                </button>

                {open && (
                    <ol style={{
                        backgroundColor: Theme.colors.white,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 12,
                        listStyle: 'none',
                        margin: 0,
                        padding: mobile ? '16px' : '20px'
                    }}>
                        {asset.transcript.map((entry, entryIndex) => {

                            let body = entry.text;
                            if(entry.link) {
                                let parts = entry.text.split(entry.link.label);
                                body = (
                                    <>
                                        {parts[0]}
                                        <span
                                        className={'text-button'}
                                        onClick={window.open.bind(this, entry.link.url)}
                                        style={{
                                            color: Theme.colors.primary(),
                                            cursor: 'pointer',
                                            fontWeight: 500,
                                            textDecoration: 'underline'
                                        }}>{entry.link.label}</span>
                                        {parts[1]}
                                    </>
                                );
                            }

                            return (
                                <li
                                key={entryIndex}
                                style={{
                                    display: 'flex',
                                    gap: 12
                                }}>
                                    <span style={{
                                        ...Theme.textStyles.caption({ color: Theme.colors.lightGrey, fontWeight: 700 }),
                                        flexShrink: 0,
                                        fontVariantNumeric: 'tabular-nums',
                                        paddingTop: 3,
                                        width: 20
                                    }}>{entryIndex + 1}</span>
                                    <span style={{
                                        ...Theme.textStyles.body({ fontSize: 15, lineHeight: 1.6 }),
                                        whiteSpace: 'normal'
                                    }}>{body}</span>
                                </li>
                            )
                        })}
                    </ol>
                )}
            </div>
        )
    }

    const getAssets = () => {
        return (assets || []).map((asset, index) => {

            let media = null;

            if(asset.type === 'image') {
                media = (
                    <div style={{
                        backgroundColor: Theme.colors.offWhite,
                        border: `1px solid ${Theme.colors.border()}`,
                        borderRadius: 16,
                        overflow: 'hidden',
                        position: 'relative',
                        width: '100%'
                    }}>
                        <div style={{ padding: mobile ? 16 : 32 }}>
                            <img
                            alt={asset.title || 'Support illustration'}
                            src={asset.url}
                            style={{
                                display: 'block',
                                height: 'auto',
                                margin: '0 auto',
                                maxWidth: 720,
                                objectFit: 'contain',
                                width: '100%'
                            }} />
                        </div>
                        {getFullscreenButton(asset)}
                    </div>
                );
            }

            if(asset.type === 'video') {
                media = (
                    <div style={{ position: 'relative', width: '100%' }}>
                        <video
                        controls={true}
                        poster={asset.poster_url}
                        preload={'none'}
                        style={{
                            backgroundColor: Theme.colors.dark(),
                            border: `1px solid ${Theme.colors.border()}`,
                            borderRadius: 16,
                            display: 'block',
                            height: 'auto',
                            objectFit: 'contain',
                            width: '100%'
                        }}>
                            <source src={asset.url} type={'video/mp4'} />
                        </video>
                        {getVideoTranscript(index)}
                    </div>
                );
            }

            if(!media) {
                return null;
            }

            return (
                <div
                key={index}
                style={{
                    borderTop: index === 0 ? 'none' : `1px solid ${Theme.colors.divider()}`,
                    paddingTop: index === 0 ? 0 : (mobile ? 28 : 36)
                }}>
                    {/*
                        The API sets a video asset's title to the article's own
                        title, and the modal header is already showing that. Two
                        identical headings, one above the other, reads as a
                        rendering fault rather than as a heading. Anything the
                        asset calls itself that is genuinely its own still
                        shows.
                    */}
                    {asset.title && sameAsArticle(asset.title) === false && (
                        <span style={{
                            ...Theme.textStyles.subHeading({ fontSize: mobile ? 17 : 19 }),
                            display: 'block',
                            marginBottom: 12
                        }}>{asset.title}</span>
                    )}
                    {asset.values?.length > 0 && (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 12,
                            marginBottom: 18
                        }}>
                            {asset.values.map((value, valueIndex) => getText(value, valueIndex))}
                        </div>
                    )}
                    {media}
                </div>
            )
        });
    }

    const getFullscreenContent = () => {
        return fullscreenContent && (
            <animated.div style={{
                alignItems: 'center',
                bottom: 0,
                display: 'flex',
                height: '100%',
                justifyContent: 'center',
                left: 0,
                opacity: animations.opacity,
                padding: mobile ? 24 : '5vw',
                position: 'fixed',
                right: 0,
                top: 0,
                width: '100%',
                zIndex: 9990
            }}>
                <div
                className={'cursor-pointer'}
                onClick={onCloseFullscreenContentClick}
                style={{
                    backgroundColor: 'rgba(0,0,0,0.75)',
                    bottom: 0,
                    height: '100%',
                    left: 0,
                    position: 'fixed',
                    right: 0,
                    top: 0,
                    width: '100%',
                    zIndex: 9991
                }} />
                <animated.img
                alt={fullscreenContent.title || 'Support illustration'}
                src={fullscreenContent.url}
                style={{
                    borderRadius: 16,
                    height: '100%',
                    maxWidth: 1250,
                    objectFit: 'contain',
                    overflow: 'hidden',
                    transform: animations.transform,
                    width: '100%',
                    zIndex: 9992
                }} />
                <button
                aria-label={'Close'}
                onClick={onCloseFullscreenContentClick}
                style={{
                    alignItems: 'center',
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    border: 'none',
                    borderRadius: 999,
                    cursor: 'pointer',
                    display: 'flex',
                    height: 44,
                    justifyContent: 'center',
                    position: 'fixed',
                    right: 24,
                    top: 24,
                    width: 44,
                    zIndex: 9993
                }}>
                    <Icon name={'x-lg'} size={18} color={'#FFFFFF'} />
                </button>
            </animated.div>
        )
    }

    useEffect(() => {
        if(fullscreenContent) {
            animationsApi.start({
                opacity: 1,
                transform: 'scale(1)'
            });
        }
    }, [fullscreenContent]);

    return (
        <article style={{
            ...Theme.styles.card(),
            boxShadow: Theme.boxShadow.card,
            display: 'flex',
            flexDirection: 'column',
            padding: mobile ? 24 : 36,
            position: 'relative',
            width: '100%',
            ...containerStyle,
            ...style
        }}>
            {title && (
                <span style={{
                    ...Theme.textStyles.heading({ fontSize: mobile ? 21 : 25 }),
                    display: 'block',
                    marginBottom: 16
                }}>{title}</span>
            )}
            {getContent()}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: mobile ? 28 : 36
            }}>
                {getAssets()}
            </div>
            {getFullscreenContent()}
        </article>
    )
}

export const Support = ({ dealership, params, utils }) => {

    const limit = 10;
    const offset = useRef(0);
    const searchTextRef = useRef(null);

    const [article, setArticle] = useState(null);

    /*
     * The guided support flow, opened from any of the three "Ask a question"
     * entry points. It used to send people to the contact form, which asked
     * them to describe a fault in a text box before anybody had helped them
     * work out what the fault was. The flow asks first and only collects
     * details if it cannot answer.
     */
    /*
     * The guided flow. `false` when closed, otherwise the node to open at:
     * `true` for the top, or a node id for a link that used to have its own
     * page. See views/site/SupportFlowModal.js.
     */
    const [supportFlow, setSupportFlow] = useState(params?.support_flow || false);

    /*
     * A retired page routed here and named the node its replacement lives at.
     * Opening on arrival is the point: somebody following an old mobile app
     * link asked about the app, and should not have to find their way back to
     * that question through a menu.
     */
    useEffect(() => {
        if(params?.support_flow) {
            setSupportFlow(params.support_flow);
        }
    }, [params?.support_flow]);

    /*
     * A link that named a section of this page rather than the page itself.
     *
     * After a tick, because the sections below are still measuring themselves
     * on the frame this mounts: articles are being fetched and the manual list
     * is laying out, so a scroll computed now lands against a page that is
     * about to change height under it.
     */
    useEffect(() => {

        if(!params?.support_scroll) {
            return;
        }

        let timer = setTimeout(() => Utils.scrollToElement(params.support_scroll), 250);
        return () => clearTimeout(timer);
    }, [params?.support_scroll]);
    const [content, setContent] = useState([]);
    const [hovered, setHovered] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paging, setPaging] = useState(null);
    const [searchText, setSearchText] = useState(null);

    const onManuallyStartSearch = () => {
        offset.current = 0;
        setArticle(null);
        setLoading('search');
        fetchResources();
    }

    const onSearchTextChange = text => {

        // reset offset for paging
        offset.current = 0;

        // update search ref and search text state
        searchTextRef.current = text;
        setSearchText(text);

        // fetch resources if all search text has been removed
        if(!text || text.length === 0) {
            setArticle(null);
            setLoading(true);
            fetchResources();
        }
    }

    const onViewMoreResultsClick = () => {
        offset.current = offset.current + limit;
        setLoading('paging');
        fetchResources();
    }

    /*
     * Presentation rewritten in the marketing surface's design system. The
     * search field is a native input rather than the legacy TextField, which
     * draws with Appearance and cannot take this page's type or radii. The
     * search behaviour — debounce on change, enter to submit, clear resets —
     * is unchanged.
     *
     * GraciSupportContent, which renders an opened article, is still on the
     * legacy system. It is shared with other views, so it is a separate job.
     */

    /*
     * The support centre, rebuilt.
     *
     * What it was: a search box, then every matching article rendered in full —
     * ten at a time, each with its images, videos and transcripts expanded
     * inline. You could not scan it, could not see what topics existed, and the
     * same videos appeared again lower down under the manuals. Someone arriving
     * with one specific problem got a wall.
     *
     * What it is now, in the order someone actually needs it:
     *
     *   1. search            — because most people arrive with a phrase in mind
     *   2. three ways in     — manuals, app help, a person. The jobs people
     *                          come here for that are not articles at all.
     *   3. an index          — titles and a one-line summary, collapsed. Open
     *                          one and only that one expands.
     *   4. still stuck       — a way to reach a human, at the bottom where it
     *                          belongs rather than competing with self-service.
     *
     * The article body is still GraciSupportContent, unchanged. The difference
     * is that it renders on request instead of all at once.
     */

    const getTag = entry => {
        let assets = entry.assets || [];
        if(assets.some(asset => asset.type === 'video')) {
            return 'Video';
        }
        if(assets.some(asset => asset.type === 'image')) {
            return 'Walkthrough';
        }
        return 'Guide';
    }

    const getSummary = entry => {
        let first = (entry.values || []).find(value => value?.text);
        return first ? Utils.truncateText(first.text, 150) : null;
    }

    /*
     * The three ways in, in the masthead.
     *
     * These are the jobs people arrive with that are not articles at all — a
     * manual, the app, a person. Below the fold they read as an afterthought;
     * in the header they are the first answer to the question the page asks.
     */
    const getQuickRoutes = () => {

        let routes = [{
            copy: 'Component diagrams, placement guidance and step-by-step instructions for every device.',
            icon: 'card-text',
            label: 'Product manuals',
            onClick: () => Utils.scrollToElement('product-manuals')
        },{
            copy: 'Chirping, cleaning, replacements, moving houses, and the mobile app.',
            icon: 'list',
            label: 'Guides and troubleshooting',
            onClick: () => Utils.scrollToElement('guides')
        },{
            copy: 'Describe what is happening and a person here will get back to you.',
            icon: 'send-fill',
            label: 'Ask a question',
            onClick: setSupportFlow.bind(this, true)
        }];

        return (
            <div style={{
                display: 'grid',
                gap: Utils.isMobile() ? 16 : 20,
                gridTemplateColumns: Utils.isMobile() ? '1fr' : 'repeat(3, 1fr)',
                margin: '0 auto',
                maxWidth: Theme.layout.maxWidth,
                textAlign: 'left'
            }}>
                {routes.map((route, index) => (
                    <button
                    key={index}
                    onClick={route.onClick}
                    style={{
                        ...Theme.styles.card(),
                        boxShadow: Theme.boxShadow.card,
                        cursor: 'pointer',
                        display: 'block',
                        padding: Utils.isMobile() ? 22 : 26,
                        textAlign: 'left',
                        width: '100%'
                    }}>
                        <div style={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: 12,
                            marginBottom: 10
                        }}>
                            <Icon color={Theme.colors.primary()} name={route.icon} size={19} />
                            <span style={Theme.textStyles.subHeading({ fontSize: 17 })}>{route.label}</span>
                        </div>
                        <span style={{
                            ...Theme.textStyles.body({ fontSize: 14 }),
                            display: 'block'
                        }}>{route.copy}</span>
                    </button>
                ))}
            </div>
        )
    }

    const getPagingComponents = () => {
        let { current_page = 0, number_of_pages = 0 } = paging || {};
        return current_page < number_of_pages && (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: 28,
                width: '100%'
            }}>
                <SiteButton
                label={'Show more articles'}
                loading={loading === 'paging'}
                onClick={onViewMoreResultsClick}
                type={'outline'} />
            </div>
        )
    }

    const getResourcesContent = () => {

        if(loading === true || loading === 'search') {
            return (
                <div style={{
                    border: `1px solid ${Theme.colors.border()}`,
                    borderRadius: 16,
                    overflow: 'hidden',
                    width: '100%'
                }}>
                    {[0, 1, 2, 3].map(index => (
                        <div
                        key={index}
                        style={{
                            borderTop: index === 0 ? 'none' : `1px solid ${Theme.colors.divider()}`,
                            padding: Utils.isMobile() ? '18px 18px' : '20px 24px'
                        }}>
                            <div className={'skeleton'} style={{ height: 16, marginBottom: 10, width: '42%' }} />
                            <div className={'skeleton'} style={{ height: 12, width: '78%' }} />
                        </div>
                    ))}
                </div>
            )
        }

        if(content.length === 0) {
            return (
                <div style={{
                    ...Theme.styles.card(),
                    boxShadow: Theme.boxShadow.card,
                    padding: Utils.isMobile() ? '40px 24px' : '56px 40px',
                    textAlign: 'center',
                    width: '100%'
                }}>
                    <div style={{
                        alignItems: 'center',
                        backgroundColor: Theme.colors.offWhite,
                        borderRadius: '50%',
                        display: 'flex',
                        height: 56,
                        justifyContent: 'center',
                        margin: '0 auto 18px',
                        width: 56
                    }}>
                        <Icon color={Theme.colors.lightGrey} name={'search'} size={20} />
                    </div>
                    <span style={{
                        ...Theme.textStyles.subHeading({ fontSize: 19 }),
                        display: 'block',
                        marginBottom: 10
                    }}>{searchText ? 'Nothing matched that search' : 'No articles yet'}</span>
                    <span style={{
                        ...Theme.textStyles.body({ fontSize: 15 }),
                        display: 'block',
                        margin: '0 auto 24px',
                        maxWidth: '52ch'
                    }}>{searchText
                        ? 'Try fewer words, or a broader phrase. If it is not here, ask us directly and a person will answer.'
                        : 'There are no support articles here yet. Ask us directly and a person will answer.'}</span>
                    <SiteButton
                    icon={'send-fill'}
                    label={'Ask a question'}
                    onClick={setSupportFlow.bind(this, true)}
                    type={'primary'} />
                </div>
            )
        }

        /*
         * A list, and only a list. Each row is a title, a kind, and a one-line
         * summary — enough to choose from. The article itself opens in a sheet,
         * because an article carries images, video and a transcript, and
         * expanding that inline pushes every other row off the screen and loses
         * the reader's place in the list they were scanning.
         */
        return (
            <div style={{
                backgroundColor: Theme.colors.white,
                border: `1px solid ${Theme.colors.border()}`,
                borderRadius: 16,
                overflow: 'hidden',
                width: '100%'
            }}>
                {content.map((entry, index) => {

                    let summary = getSummary(entry);

                    return (
                        <button
                        key={index}
                        onClick={setArticle.bind(this, entry)}
                        onMouseEnter={setHovered.bind(this, index)}
                        onMouseLeave={setHovered.bind(this, null)}
                        style={{
                            alignItems: 'center',
                            backgroundColor: hovered === index ? Theme.colors.offWhite : 'transparent',
                            border: 'none',
                            borderTop: index === 0 ? 'none' : `1px solid ${Theme.colors.divider()}`,
                            cursor: 'pointer',
                            display: 'flex',
                            gap: 16,
                            justifyContent: 'space-between',
                            padding: Utils.isMobile() ? '18px 18px' : '20px 24px',
                            textAlign: 'left',
                            transition: 'background-color 0.15s ease',
                            width: '100%'
                        }}>
                            <div style={{ minWidth: 0 }}>
                                <div style={{
                                    alignItems: 'baseline',
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 10,
                                    marginBottom: summary ? 5 : 0
                                }}>
                                    <span style={Theme.textStyles.subHeading({ fontSize: Utils.isMobile() ? 16 : 17 })}>
                                        {entry.title}
                                    </span>
                                    <span style={{
                                        backgroundColor: Theme.colors.offWhite,
                                        borderRadius: 999,
                                        color: Theme.colors.subText(),
                                        fontFamily: Theme.fonts.body,
                                        fontSize: 11,
                                        fontWeight: 700,
                                        letterSpacing: '0.06em',
                                        padding: '2px 8px',
                                        textTransform: 'uppercase'
                                    }}>{getTag(entry)}</span>
                                </div>
                                {summary && (
                                    <span style={Theme.textStyles.body({ fontSize: 14 })}>{summary}</span>
                                )}
                            </div>
                            <Icon
                            color={Theme.colors.primary()}
                            name={'arrow-right'}
                            size={14} />
                        </button>
                    )
                })}
            </div>
        )
    }

    const getSearchComponents = () => {
        return (
            <div style={{ width: '100%' }}>
                <div style={{
                    alignItems: 'center',
                    backgroundColor: Theme.colors.white,
                    border: `1px solid ${Theme.colors.border()}`,
                    borderRadius: 999,
                    display: 'flex',
                    gap: 10,
                    padding: Utils.isMobile() ? '6px 12px 6px 16px' : '8px 10px 8px 20px'
                }}>
                    <Icon color={Theme.colors.lightGrey} name={'search'} size={16} />
                    <input
                    onChange={evt => onSearchTextChange(evt.target.value)}
                    onKeyDown={evt => evt.key === 'Enter' && onManuallyStartSearch()}
                    placeholder={'Search for an issue...'}
                    style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: Theme.colors.text(),
                        flexGrow: 1,
                        fontFamily: Theme.fonts.body,
                        fontSize: 16,
                        minWidth: 0,
                        outline: 'none',
                        padding: '10px 0'
                    }}
                    value={searchText || ''} />
                    {searchText && searchText.length > 0 && (
                        <span
                        className={'text-button'}
                        onClick={onSearchTextChange.bind(this, null)}
                        style={{
                            ...Theme.textStyles.caption({ color: Theme.colors.subText(), fontWeight: 600 }),
                            cursor: 'pointer',
                            flexShrink: 0,
                            padding: '0 6px'
                        }}>{'Clear'}</span>
                    )}
                    <SiteButton
                    icon={Utils.isMobile() ? 'arrow-right' : null}
                    label={Utils.isMobile() ? null : 'Search'}
                    loading={loading === 'search'}
                    onClick={onManuallyStartSearch}
                    style={{
                        flexShrink: 0,
                        fontSize: 14,
                        padding: Utils.isMobile() ? '10px 16px' : '11px 24px'
                    }}
                    type={'primary'} />
                </div>
            </div>
        )
    }

    const fetchResources = async () => {
        try {

            // fetch graci support content from server
            let { content, paging } = await Request.get(utils, '/utils/', {
                limit: limit,
                offset: offset.current,
                search_text: searchTextRef.current,
                type: 'replicated_website_support_resources_content'
            });

            // format entry results
            let entries = content.map(entry => ({
                ...entry,
                date: moment.utc(entry.date).local()
            }));

            // append results to local state if paging has advanced past the first page
            setContent(results => offset.current > 0 ? results.concat(entries) : entries);

            // end loading and update state for paging props
            setLoading(false);
            setPaging(paging);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the support resources list. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {
        setLoading(true);
        fetchResources();
    }, []);

    /*
     * A section heading used by both lists, so guides and manuals read as two
     * of the same thing rather than two different pages stapled together. The
     * manuals used to arrive under a centred eyebrow-and-subtitle block twice
     * the size of the guides heading, which is most of why the page felt like
     * it changed subject halfway down.
     */
    // A water-only dealership carries three products, not six — the count has
    // to follow the catalogue rather than be written down.
    let manualCount = Catalog.getAll(Catalog.getMode(dealership)).length;

    const getListHeading = (title, meta) => (
        <div style={{
            alignItems: 'baseline',
            borderBottom: `1px solid ${Theme.colors.border()}`,
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: 18,
            paddingBottom: 12
        }}>
            <span style={Theme.textStyles.subHeading({ fontSize: 19 })}>{title}</span>
            {meta && (
                <span style={Theme.textStyles.caption({ color: Theme.colors.subText() })}>{meta}</span>
            )}
        </div>
    );

    return (
        <div
        className={'site'}
        style={{ width: '100%' }}>
            <NavigationSectionHeader
            dealership={dealership}
            eyebrow={'Support'}
            identifier={'support'}
            subtitle={'Find a guide, open a manual, or ask us directly. Most of what comes up has a short answer, and the guides below are written to give you it in a couple of minutes rather than a phone call. If yours is not among them, the questions will work out what is happening and put you in front of a person who already knows what you have tried.'}
            title={'How can we help?'}
            utils={utils}>
                {getQuickRoutes()}
            </NavigationSectionHeader>

            <div style={{
                margin: '0 auto',
                maxWidth: Theme.layout.readableWidth,
                padding: Theme.layout.sectionPadding(),
                width: '100%'
            }}>
                {/* Guides. The search sits with the thing it searches, in the
                    masthead it read as a search of the whole site.

                    `scroll-margin-top` is belt and braces: it makes an anchor
                    land correctly even for a scroll that did not go through
                    Utils.scrollToElement, such as a browser restoring a #guides
                    fragment on load. */}
                <div id={'guides'} style={{ scrollMarginTop: Theme.layout.headerOffset() + 24 }}>
                    {getListHeading(
                        searchText && searchText.length > 0 ? `Results for “${searchText}”` : 'Guides & troubleshooting',
                        content.length > 0 && loading === false ? `${content.length} article${content.length === 1 ? '' : 's'}` : null
                    )}

                    <div style={{ marginBottom: 18 }}>
                        {getSearchComponents()}
                    </div>

                    {getResourcesContent()}
                    {getPagingComponents()}
                </div>

                {/* Manuals. Same width, same heading, same rows. */}
                <div
                id={'product-manuals'}
                style={{
                    marginTop: Utils.isMobile() ? 48 : 64,
                    scrollMarginTop: Theme.layout.headerOffset() + 24
                }}>
                    {getListHeading('Product manuals', `${manualCount} device${manualCount === 1 ? '' : 's'}`)}
                    <ProductManuals utils={utils} />
                </div>

                {/* The last resort, at the end, where it belongs — rather than
                    competing with self-service at the top. */}
                <div style={{
                    alignItems: Utils.isMobile() ? 'flex-start' : 'center',
                    backgroundColor: Theme.colors.white,
                    border: `1px solid ${Theme.colors.border()}`,
                    borderRadius: 16,
                    display: 'flex',
                    flexDirection: Utils.isMobile() ? 'column' : 'row',
                    gap: 16,
                    justifyContent: 'space-between',
                    marginTop: Utils.isMobile() ? 40 : 56,
                    padding: Utils.isMobile() ? '24px 22px' : '26px 28px'
                }}>
                    <div>
                        <span style={{
                            ...Theme.textStyles.subHeading({ fontSize: 17 }),
                            display: 'block',
                            marginBottom: 4
                        }}>{'Still stuck?'}</span>
                        <span style={Theme.textStyles.body({ fontSize: 14 })}>
                            {`Describe what is happening and ${dealership?.name || 'we'} will get back to you within one business day.`}
                        </span>
                    </div>
                    <SiteButton
                    icon={'send-fill'}
                    label={'Ask a question'}
                    onClick={setSupportFlow.bind(this, true)}
                    style={{ flexShrink: 0, width: Utils.isMobile() ? '100%' : 'auto' }}
                    type={'primary'} />
                </div>
            </div>

            <SupportFlowModal
            dealership={dealership}
            entry={typeof(supportFlow) === 'string' ? supportFlow : null}
            onClose={setSupportFlow.bind(this, false)}
            open={Boolean(supportFlow)} />

            <Modal
            eyebrow={article ? getTag(article) : null}
            onClose={setArticle.bind(this, null)}
            open={article !== null}
            title={article?.title || ''}
            width={820}>
                {article && (
                    <GraciSupportContent
                    {...article}
                    headingShown={article.title}
                    style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                    title={null}
                    utils={utils} />
                )}
            </Modal>
        </div>
    )
}
